import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layouts/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  CreditCard, CheckCircle2, XCircle, Clock, Eye, 
  Loader2, RefreshCw, Crown, Search
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLicenses = () => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchLicenses();
  }, []);

  const fetchLicenses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/licenses`);
      setLicenses(response.data);
    } catch (error) {
      toast.error('Failed to load licenses');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (licenseId) => {
    setProcessing(true);
    try {
      await axios.put(`${API_URL}/api/admin/licenses/${licenseId}/activate`);
      toast.success('License activated successfully');
      fetchLicenses();
      setViewDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate license');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setProcessing(true);
    try {
      await axios.put(`${API_URL}/api/admin/licenses/${selectedLicense.id}/reject?reason=${encodeURIComponent(rejectionReason)}`);
      toast.success('License rejected');
      setRejectDialogOpen(false);
      setRejectionReason('');
      fetchLicenses();
      setViewDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject license');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTierBadge = (tier) => {
    const config = {
      organized: { label: 'Organized', className: 'bg-blue-100 text-blue-700' },
      full_transparency: { label: 'Full Transparency', className: 'bg-purple-100 text-purple-700' }
    };
    const c = config[tier] || { label: tier, className: 'bg-gray-100 text-gray-700' };
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { icon: Clock, label: 'Pending', className: 'bg-amber-100 text-amber-700' },
      active: { icon: CheckCircle2, label: 'Active', className: 'bg-green-100 text-green-700' },
      rejected: { icon: XCircle, label: 'Rejected', className: 'bg-red-100 text-red-700' }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={c.className}>
        <Icon className="h-3 w-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  const filteredLicenses = licenses.filter(lic => {
    const matchesFilter = filter === 'all' || lic.status === filter;
    const matchesSearch = !searchTerm || 
      lic.event_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lic.organizer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lic.organizer_email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const pendingCount = licenses.filter(l => l.status === 'pending').length;
  const activeCount = licenses.filter(l => l.status === 'active').length;
  const totalRevenue = licenses
    .filter(l => l.status === 'active')
    .reduce((sum, l) => sum + (l.amount || 0), 0);

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in" data-testid="admin-licenses-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-3">
              <Crown className="h-8 w-8 text-gold" />
              License Management
            </h1>
            <p className="text-gray-400 mt-1">Review and manage reunion license purchases</p>
          </div>
          
          <Button 
            variant="outline" 
            onClick={fetchLicenses}
            className="gap-2 border-gray-700 text-gray-300 hover:bg-gray-800"
            data-testid="refresh-licenses-btn"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-500">{pendingCount}</p>
              <p className="text-sm text-gray-400">Pending Review</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-500">{activeCount}</p>
              <p className="text-sm text-gray-400">Active Licenses</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-gold">{formatCurrency(totalRevenue)}</p>
              <p className="text-sm text-gray-400">Total Revenue</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <Tabs value={filter} onValueChange={setFilter}>
                <TabsList className="bg-gray-800">
                  <TabsTrigger value="all" className="data-[state=active]:bg-gray-700">All</TabsTrigger>
                  <TabsTrigger value="pending" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
                    Pending ({pendingCount})
                  </TabsTrigger>
                  <TabsTrigger value="active" className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400">Active</TabsTrigger>
                  <TabsTrigger value="rejected" className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400">Rejected</TabsTrigger>
                </TabsList>
              </Tabs>
              
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  placeholder="Search events or organizers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* License Table */}
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gold" />
              </div>
            ) : filteredLicenses.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <CreditCard className="h-12 w-12 mx-auto mb-3 text-gray-700" />
                <p>No license purchases found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800 hover:bg-gray-800/50">
                    <TableHead className="text-gray-400">Event</TableHead>
                    <TableHead className="text-gray-400">Organizer</TableHead>
                    <TableHead className="text-gray-400">Tier</TableHead>
                    <TableHead className="text-gray-400">Amount</TableHead>
                    <TableHead className="text-gray-400">Payment</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400">Date</TableHead>
                    <TableHead className="text-gray-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLicenses.map((license) => (
                    <TableRow key={license.id} className="border-gray-800 hover:bg-gray-800/50">
                      <TableCell>
                        <div>
                          <p className="font-medium text-white">{license.event_name}</p>
                          <p className="text-xs text-gray-500">{license.school_name}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-gray-300">{license.organizer_name}</p>
                          <p className="text-xs text-gray-500">{license.organizer_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{getTierBadge(license.tier)}</TableCell>
                      <TableCell className="font-bold text-gold">{formatCurrency(license.amount)}</TableCell>
                      <TableCell className="uppercase text-xs text-gray-400">{license.payment_method}</TableCell>
                      <TableCell>{getStatusBadge(license.status)}</TableCell>
                      <TableCell className="text-gray-400 text-sm">{formatDate(license.purchased_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedLicense(license);
                            setViewDialogOpen(true);
                          }}
                          className="text-gray-400 hover:text-white hover:bg-gray-800"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View License Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-gold">License Details</DialogTitle>
            <DialogDescription className="text-gray-400">
              Review purchase information and proof of payment
            </DialogDescription>
          </DialogHeader>

          {selectedLicense && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Event</p>
                  <p className="font-medium">{selectedLicense.event_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">School</p>
                  <p className="font-medium">{selectedLicense.school_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Organizer</p>
                  <p className="font-medium">{selectedLicense.organizer_name}</p>
                  <p className="text-xs text-gray-500">{selectedLicense.organizer_email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Tier</p>
                  {getTierBadge(selectedLicense.tier)}
                </div>
                <div>
                  <p className="text-xs text-gray-500">Amount</p>
                  <p className="font-bold text-gold text-lg">{formatCurrency(selectedLicense.amount)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Payment Method</p>
                  <p className="font-medium uppercase">{selectedLicense.payment_method}</p>
                </div>
              </div>

              {selectedLicense.reference_number && (
                <div>
                  <p className="text-xs text-gray-500">Reference Number</p>
                  <p className="font-mono bg-gray-800 px-3 py-2 rounded">{selectedLicense.reference_number}</p>
                </div>
              )}

              {selectedLicense.proof_image && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Proof of Payment</p>
                  <img 
                    src={selectedLicense.proof_image} 
                    alt="Payment Proof" 
                    className="rounded-lg max-h-48 w-full object-contain bg-gray-800"
                  />
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                <p className="text-xs text-gray-500">
                  Submitted: {formatDate(selectedLicense.purchased_at)}
                </p>
                {getStatusBadge(selectedLicense.status)}
              </div>

              {selectedLicense.status === 'pending' && (
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => handleActivate(selectedLicense.id)}
                    disabled={processing}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    Activate License
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setRejectDialogOpen(true)}
                    className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </div>
              )}

              {selectedLicense.status === 'rejected' && selectedLicense.rejection_reason && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-xs text-red-400 mb-1">Rejection Reason:</p>
                  <p className="text-red-300">{selectedLicense.rejection_reason}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-md bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400">Reject License Purchase</DialogTitle>
            <DialogDescription className="text-gray-400">
              Please provide a reason for rejection. The organizer will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="e.g., Invalid reference number, payment not received, amount mismatch..."
              className="bg-gray-800 border-gray-700 text-white"
              rows={3}
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setRejectDialogOpen(false)}
                className="flex-1 border-gray-700 text-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                disabled={processing}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Confirm Rejection
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminLicenses;
